import React, { useRef, useEffect, useCallback } from 'react';
import { Card, Col, Row, Typography } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Plugin, ChartOptions, ChartData, ChartTypeRegistry } from 'chart.js';
import { formatAmount } from 'app/utils/amountFormatter';

ChartJS.register(ArcElement, Tooltip, Legend);

interface DoughnutChartCardProps {
  title: string;
  data: { name: string; value: number; currency?: string }[];
  colors: string[];
}

const DoughnutChartCard: React.FC<DoughnutChartCardProps> = React.memo(({ title, data, colors }) => {
  const chartRef = useRef<any>(null);

  const centerTextPlugin: Plugin<keyof ChartTypeRegistry, any> = {
    id: 'centerTextPlugin',
    beforeDraw(chart) {
      const ctx = chart.ctx;
      const width = chart.width;
      const height = chart.height;

      const totalValue = chart.data.datasets[0].data.reduce((acc: number, val: number) => acc + val, 0);

      const fontSize = 0.1 * Math.min(width, height);
      ctx.save();
      ctx.font = `${fontSize - 10}px 'Arial', sans-serif`;
      ctx.fillStyle = '#2c3e50';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 10;

      ctx.fillText(formatAmount(totalValue.toString()), width / 2, height / 2);
      ctx.restore();
    },
  };

  const chartData: ChartData<'doughnut'> = {
    labels: data.map(item => item.name),
    datasets: [
      {
        data: data.map(item => item.value),
        backgroundColor: colors,
        borderWidth: 1,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      tooltip: { enabled: true },
    },
    cutout: '60%',
  };

  useEffect(() => {
    ChartJS.register(centerTextPlugin);

    return () => {
      ChartJS.unregister(centerTextPlugin);
      ChartJS.registry.remove(centerTextPlugin);
    };
  }, []);

  const updateChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.chartInstance?.update();
    }
  }, [data]);

  useEffect(() => {
    updateChart();
  }, [updateChart]);

  const totalValue = data.reduce((acc, item) => acc + item.value, 0);

  const suffix = data.find(item => item.currency)?.currency;

  const totalWithSuffix = suffix ? formatAmount(totalValue.toString()) + ` ${suffix}` : formatAmount(totalValue.toString());

  return (
    <Col xs={{ span: 24 }} lg={{ span: 12 }} style={{ padding: '0 8px' }}>
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>{title}</Col>
            <Col>
              <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
                {totalWithSuffix}
              </Typography.Text>
            </Col>
          </Row>
        }
        className="card"
      >
        <div style={{ position: 'relative', width: '100%', height: '230px' }}>
          <Doughnut ref={chartRef} data={chartData} options={options} />
        </div>
      </Card>
    </Col>
  );
});

export default DoughnutChartCard;
